import React, { useState, useContext, useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Drawer,
  Hidden,
  IconButton,
  Button,
  Toolbar,
  Typography,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SidebarMenu from "../components/SidebarMenu/SidebarMenu";
import AuthContext from "../hooks/authContext";
import CommonContext from "../hooks/commonContext";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Footer from "../components/footer/footer";
import Header from "../components/header/header";
import Loader from "../components/AppLoader/Loader";
import { useMediaQuery } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { useTour } from "../components/ui/TourContext";
import { Moon, Sun } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { CommonConfirmationDialog } from "../components/DialogComponent/SupplyDataDialog";
import { setAnyUserData } from "../services/apiservices/employee";
import ReEngageModal from "../components/ReEngageModal";
import { updateSettings } from "../services/apiservices/settings";
// const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      // width: drawerWidth,
      flexShrink: 0,
    },
  },
  logoutLink: {
    position: "fixed",
    bottom: 0,
    // width: 220,
    left: 0,
    // paddingLeft: 10,
    cursor: "pointer",
    // backgroundColor:'#343a45',
    "&:hover": {
      backgroundColor: "#00000044",
      cursor: "pointer",
    },
    "@media (max-width: 767px)": {
      width: 239,
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: "none",
    borderBottomColor: "#334155",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    backgroundColor: "#2c3440"
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  toolbar: {
    ...theme.mixins.toolbar,
    // backgroundColor: "#2C3440",
    minHeight: "auto",
  },
  toolbarmirror: { ...theme.mixins.toolbar, backgroundColor: "transparent" },
  drawerPaper: {
    // width: drawerWidth,
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    backgroundColor: "#2C3440",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  drawerPaper2: {
    // width: drawerWidth,
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    // backgroundColor: "#2C3440",
    marginTop: "47px",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  drawerPaperDark: {
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    backgroundColor: "#1A1F2C",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  content: {
    flexGrow: 1,
  },
  btnLogout: {
    color: "#CBD5E1",
    fontSize: "14px",
    fontWeight: "700",
    "@media (max-width: 767px)": {
      fontSize: "19px",
    },
  },
}));

const DefaultLayouts = ({ isLoading, ...props }) => {
  const auth = useContext(AuthContext);
  const [layoutLoader, setLayoutLoader] = useState(false);
  const isExpanded = auth.isExpanded;
  const classes = useStyles();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [change, setChange] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const { stopTour } = useTour();
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const darkModePreference = localStorage?.getItem('darkMode') === 'true';
    setIsDarkMode(darkModePreference);
  }, []);

  function getRemainingDays(currentPeriodEnd, popup = false) {
    const today = new Date();
    const endDate = new Date(currentPeriodEnd * 1000);

    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const timeDiff = endDate - today;
    const daysLeft = Math.max(Math.ceil(timeDiff / (1000 * 60 * 60 * 24)), 0);

    if (popup === true) {
      return daysLeft;
    } else {
      return (
        <span style={{ color: "#D97706", fontSize: "14px", fontWeight: "500" }}>
          {`${daysLeft} Day${daysLeft !== 1 ? "s" : ""}`}
        </span>
      );
    }
  }

  useEffect(() => {
    const isCompanyUser = auth?.userProfile?.role === "companyUser";
    const noCards = auth?.userProfile?.cardsData?.cards && auth?.userProfile?.cardsData?.cards?.length === 0;
    const hidePopup = auth?.userProfile?.hideAddPaymentPopup;
    const remainingDays = getRemainingDays(auth?.userProfile?.subscriptionDetails?.current_period_end, true);

    if (!isCompanyUser) {
      setOpenPaymentDialog(false);
      return;
    }

    if (remainingDays <= 7 && noCards) {
      setOpenPaymentDialog(true);
      return;
    } else {
      setOpenPaymentDialog(false);
      return;
    }
  }, [auth?.userProfile]);

  const logoutuser = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (auth?.userProfile?.role === "companyUser") {
      const data = { hideAddPaymentPopup: false }
      await setAnyUserData(auth?.userProfile?.id, data);
    }

    auth.logoutuser();
    stopTour();
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('clientId')
    localStorage.removeItem('companyId')
    localStorage.removeItem('clients')
    localStorage.removeItem('companies')
    localStorage.setItem('darkMode', 'false');

    if (auth?.userProfile?.clientsLength === 0 && auth?.userProfile?.employeesLength === 0) {
      localStorage.removeItem("hasSeenTour");
    } else {
      localStorage.setItem("hasSeenTour", "true");
    }
  };

  const handleClick = () => {
    navigate("/account/profile");
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === "/dashboard" || path === "/account/profile") {
      setMobileOpen(false);
    }
  }, [location.pathname]);

  const SubBar = auth?.status === true && auth?.userProfile?.role === "companyUser" && auth?.userProfile?.subscriptionDetails?.status === "trialing" ? true : false;
  const cardBar = auth?.userProfile?.role === "companyUser" && auth?.userProfile?.cardsData?.cards?.length === 0;
  const isDeletionScheduled = auth?.userProfile?.companyData && auth?.userProfile?.companyData?.data?.delete === true;


  const toggleDarkMode = (darkMode) => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
    setIsDarkMode(darkMode);
  };

  const Role =
    auth?.userProfile?.role === "companyUser"
      ? "Company Manager"
      : (Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("admin") : auth?.userProfile?.role === "admin")
        ? "Sys. Admin"
        : (Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("Employee") : auth?.userProfile?.role === "Employee")
          ? auth?.userProfile?.employeerole === "Admin"
            ? "Team Admin"
            : auth?.userProfile?.employeerole
          : auth?.userProfile?.role;

  const drawer = (
    <div
      className={`${isMobile
          ? ""
          : isExpanded
            ? ""
            : change
              ? "drawerChangeWidth"
              : "drawerWidth"
        }`}
    >
      {auth?.status !== true && <Navigate to="/login" />}
      <div className={classes.toolbar} />
      <div className="d-flex flex-direction-column position-relative" style={{ height: "100%", marginBottom: "60px" }}>
        {isMobile && (
          <div className="pv-10 ph-15 profile  dark:bg-[#1A1F2C]">
            <Link to="/account/profile" className="d-flex flex-center">
              <div className="mr-10">
                <ListItemAvatar style={{ minWidth: "20px" }}>
                  <Avatar
                    variant="circle"
                    className="circularName"
                    alt="Remy Sharp"
                  >{`${auth?.userProfile?.firstName?.[0] ?? ""}${auth?.userProfile?.lastName?.[0] ?? ""
                    }`}</Avatar>
                </ListItemAvatar>
              </div>
              <div className="nameProfile">
                {`${auth.userProfile?.firstName} ${auth.userProfile?.lastName}`}
              </div>
            </Link>
          </div>
        )}
        <div className={isDeletionScheduled ? "pointer-events-none opacity-60" : ""}>
          <SidebarMenu handleDrawerToggle={handleDrawerToggle} change={change} />
        </div>
        {!isExpanded && !change ? (
          <div className={isMobile ? "py-2 w-[230px] border-b border-slate-700 d-flex flex-center justify-between pl-12 dark:bg-[#1A1F2C]" : "py-2 w-[245px] mb-16 border-b border-slate-700 d-flex flex-center justify-between pl-12 dark:bg-[#1A1F2C]"}
            style={{
              bottom: isMobile ? "50px" : "68px",
              position: "fixed",
              cursor: "pointer",
              height: "50px",
              borderTop: "1px solid #334155",
              backgroundColor: !isDarkMode ? "#2C3440" : "",
            }}>
            <div className="flex items-center justify-between">
              {isDarkMode ? <Moon size={20} color="white" /> : <Sun size={20} color="white" />}
              {<span className="ml-8" style={{ fontSize: "16px", color: "white" }}>Dark Mode</span>}
            </div>
            {<Switch checked={isDarkMode} onCheckedChange={() => toggleDarkMode(!isDarkMode)} />}
          </div>
        ) : (null)}
        {change && (
          <div className="pl-5" style={{
            bottom: "30px",
            position: "fixed",
            cursor: "pointer",
            height: "50px",
            backgroundColor: !isDarkMode ? "#2C3440" : "",
          }}>
            <Switch checked={isDarkMode} onCheckedChange={() => toggleDarkMode(!isDarkMode)} />
          </div>
        )}
        {!isMobile && !isExpanded && !change ? (
          <>
            <div
              className="d-flex flex-center pl-12 logoutLink dark:bg-[#1A1F2C]"
              style={{
                bottom: "35px",
                position: "fixed",
                cursor: "pointer",
                height: "auto", // 50px
                borderTop: "1px solid #334155",
                backgroundColor: !isDarkMode ? "#2C3440" : "",
                paddingTop: "10px",
                paddingBottom: "12px"
              }}
              onClick={["admin", "Company Manager", "Client"].includes(Role) || ((Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("Employee") : auth?.userProfile?.role === "Employee") && auth?.userProfile?.employeerole === "Admin") ? handleClick : null}
            >
              <ListItemAvatar style={{ minWidth: "35px" }}>
                <Avatar
                  variant="circle"
                  className="circularName"
                  alt="Remy Sharp"
                >{`${auth?.userProfile?.firstName?.[0] ?? ""}${auth?.userProfile?.lastName?.[0] ?? ""
                  }`}</Avatar>
              </ListItemAvatar>
              <Typography style={{ fontSize: "0.85rem", lineHeight: "1.2" }} className={["admin", "Company Manager", "Client"].includes(Role) ? "color-white" : "color-white"}>
                <div>
                  <span className="text-underLine">{`${auth?.userProfile?.firstName} ${auth?.userProfile?.lastName}`}</span>
                  <span className="block">{`(${Role})`}</span>
                </div>
              </Typography>
            </div>
          </>
        ) : null}
        {isExpanded && !isMobile ? null : (
          <>
            <div
              style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
              className={`${classes.logoutLink}
          ${isMobile
                  ? "pl-10 logoutLink dark:bg-[#1A1F2C]"
                  : !isExpanded && change
                    ? "drawerChangeWidth"
                    : isExpanded
                      ? ""
                      : "pl-10 logoutLink dark:bg-[#1A1F2C]"
                }`}
            >
              <Button
                className={`${classes.btnLogout} ${isExpanded
                    ? "collapsed"
                    : !isExpanded && change
                      ? "logoutMinWidth pv-6 ph-20"
                      : ""
                  }`}
                variant="text"
                onClick={logoutuser}
                startIcon={isExpanded && !isMobile ? null : <ExitToAppIcon />}
              >
                {(isExpanded || change) && !isMobile ? null : "Logout"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const daysLeft = getRemainingDays(auth?.userProfile?.subscriptionDetails?.current_period_end, false);

  const handleClosePaymentDialog = async () => {
    setOpenPaymentDialog(false);

    const data = { hideAddPaymentPopup: true }
    await setAnyUserData(auth?.userProfile?.id, data);
  };

  const handleReEngage = async () => {
    try {
      await updateSettings(
        {
          delete: false,
          deletedAt: null,
          deletedBy: null,
          reEngagedAt: new Date(),
        },
        "users",
        auth?.userProfile?.id
      );

      await logoutuser();
    } catch (error) {
      console.error("Re-engage failed", error);
    }
  };


  return (
    <>

      {auth?.userProfile && isDeletionScheduled && (
        <ReEngageModal
          open={isDeletionScheduled}
          onConfirm={handleReEngage}
          onLogout={logoutuser}
          auth={auth?.userProfile}
        />
      )}

      {(SubBar === true || cardBar === true) && (
        <div
          className="p-13 d-flex flex-center flex-space-between"
          style={{
            backgroundColor: "#FFFBEB",
            zIndex: "1000",
            position: "fixed",
            width: "100%",
            fontSize: "16px",
            lineHeight: "20px",
            paddingLeft: "20px",
            paddingRight: "20px",
            top: 0,
          }}
        >
          <div 
            className="d-flex flex-center" 
            style={{ 
              margin: "0 auto",
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? '8px' : '0',
              width: '100%',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {SubBar === true && (
              <div className="d-flex flex-center" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                <WarningAmberOutlinedIcon
                  fontSize="small"
                  className="mr-5"
                  style={{ color: "rgb(245, 158, 11)" }}
                />

                <span
                  style={{
                    color: "#020817",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                  className="mr-3"
                >
                  Your trial ends in {daysLeft}
                </span>

                <Link
                  to="/account/upgrade"
                  className="ml-8"
                  style={{
                    color: "#16A34A",
                    fontSize: "14px",
                    fontWeight: "500",
                    textDecoration: "underline",
                  }}
                >
                  Upgrade Now
                </Link>
              </div>
            )}

            {cardBar === true && (
              <div 
                className="d-flex flex-center" 
                style={{ 
                  position: isMobile ? 'relative' : (SubBar === true ? 'absolute' : 'relative'),
                  right: isMobile ? 'auto' : (SubBar === true ? '20px' : 'auto'),
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <CreditCardOutlinedIcon
                  fontSize="small"
                  className="mr-5"
                  style={{ color: "#2563EB" }}
                />
                <span
                  style={{
                    color: "#020817",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                  className="mr-8"
                >
                  Please
                </span>

                <Link
                  to="/account/companySubscription"
                  style={{
                    color: "#2563EB",
                    fontSize: "14px",
                    fontWeight: "500",
                    textDecoration: "underline",
                  }}
                >
                  Add Payment Method
                </Link>
                <span 
                  className="ml-8" 
                  style={{ 
                    color: "#020817", 
                    fontSize: "14px", 
                    fontWeight: "500",
                  }}
                >
                  before trial expires
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {auth?.userProfile?.role === "companyUser" && openPaymentDialog && (
        <CommonConfirmationDialog
          open={openPaymentDialog}
          onConfirm={() => { setOpenPaymentDialog(false); navigate("/account/companySubscription"); }}
          onClose={handleClosePaymentDialog}
          title="Complete Your Billing Setup"
          message="Please add a payment method to avoid service interruption when your trial ends."
          buttonTitle="Add Payment Method"
        />
      )}

      <CommonContext.Provider
        value={{
          loader: layoutLoader,
          setLoader: setLayoutLoader,
        }}
      >
        <div className={`DefaultContainer min-h-screen dark:bg-[#1A1F2C]`}>
          <div className={classes.root}>
            <CssBaseline />
            {auth?.status === true && !isExpanded && (
              <AppBar
                position="fixed"
                className={`${classes.appBar} pr-0 dark:bg-[#1A1F2C]`}
                style={{
                  width: !isMobile ? (change ? "40px" : "250px") : "",
                  marginTop:
                  SubBar === true && cardBar === true && isMobile
                    ? "65px"
                    : (SubBar === true || cardBar === true) && isMobile
                    ? "47px"
                    : (SubBar === true || cardBar === true) && !isMobile
                    ? "40px"
                    : "0px",
                  right: !isExpanded && !isMobile ? "inherit" : 0,
                }}
              >
                <Toolbar
                  className={classes.toolbar}
                  style={{ minHeight: !isMobile ? "" : "40px" }}
                >
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    className={classes.menuButton}
                  >
                    <MenuIcon fontSize="large" />
                  </IconButton>
                  <Header
                    history={props.history}
                    setChange={setChange}
                    change={change}
                  />
                </Toolbar>
              </AppBar>
            )}
            {auth?.status === true && (
              <>
                <nav
                  className={`${classes.drawer} ${isMobile
                      ? ""
                      : isExpanded
                        ? ""
                        : change
                          ? "drawerChangeWidth"
                          : "drawerWidth"
                    }`}
                  aria-label=" folders"
                >
                  <Hidden smUp implementation="css">
                    <Drawer
                      variant="temporary"
                      anchor={theme.direction === "rtl" ? "right" : "left"}
                      open={mobileOpen}
                      onClose={handleDrawerToggle}
                      classes={{
                        paper: isDarkMode ? classes.drawerPaperDark : classes.drawerPaper,
                      }}
                      ModalProps={{
                        keepMounted: true,
                      }}
                      // style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
                    >
                      {drawer}
                    </Drawer>
                  </Hidden>
                  <Hidden xsDown implementation="css">
                    <Drawer
                      classes={{
                        paper:
                          (SubBar === true || cardBar === true)
                            ? classes.drawerPaper2
                            : classes.drawerPaper,
                      }}
                      variant="permanent"
                      open
                      style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
                    >
                      {drawer}
                    </Drawer>
                  </Hidden>
                </nav>
              </>
            )}
            <main
              className={classes.content} 
              style={{ 
                marginTop: SubBar === true && cardBar === true && isMobile 
                  ? "65px"
                  : (SubBar === true || cardBar === true) && isMobile
                  ? "47px"
                  : (SubBar === true || cardBar === true) && !isMobile
                  ? "40px"
                  : "0px", position: "relative" 
              }}
            >
              {(layoutLoader === true || isLoading === true) && <Loader layoutLoader={layoutLoader} />}
              <div
                className={classes.toolbarmirror}
                style={{ minHeight: !isMobile ? "0px" : "40px" }}
              />
              <div className="content-wrap MuiContainer-root">
                {!isDeletionScheduled && props.children}
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </CommonContext.Provider>
    </>
  );
};

export default DefaultLayouts;
